import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useStatsigClient } from "@statsig/react-bindings";
import { useSelector } from "react-redux";
import { selectOrganization, selectIsOperatorSession } from "../store/slices/authUserSlice";
import NavItem from "./NavItem";
import NavGroup from "./NavGroup";
import { ReactComponent as CalendarIcon } from "../assets/icons/calendar.svg";
import { ReactComponent as SettingsIcon } from "../assets/icons/settings.svg";
import { ReactComponent as ListIcon } from "../assets/icons/list.svg";
import { ReactComponent as PencilIcon } from "../assets/icons/pencil.svg";
import { ReactComponent as SubscribersIcon } from "../assets/icons/subscribers.svg";
import { ReactComponent as MicIcon } from "../assets/icons/mic.svg";
import { ReactComponent as GrowIcon } from "../assets/icons/grow.svg";
import { ReactComponent as FlourIcon } from "../assets/icons/flour.svg";
import { Rocket, Filter, GitBranch, MessageSquare, Mail, BookOpen, Users, Wrench } from "lucide-react";
import { isWhiteLabel } from "../services/agencyService";

const Sidebar: React.FC = () => {
  const location = useLocation();
  const [activePage, setActivePage] = useState(location.pathname);

    // for navigating between components
    useEffect(() => {
      const path = location.pathname.split('/')[1];
      const currentPath = path ? `/${path}` : location.pathname;

      setActivePage(currentPath);
    }, [location.pathname]);

  const { checkGate } = useStatsigClient();
  const shouldHideAnalytics = checkGate("should_hide_analytics");
  const organization = useSelector(selectOrganization);
  const hasInterviews = !!organization?.feature_flags?.content_interviews;
  const isOperator = useSelector(selectIsOperatorSession);

  const emailsActive = ["/dashboard", "/journeys"].includes(activePage);
  const contentActive = ["/blueprints", "/sources", "/content-interviews", "/interviewer", "/self-interviews", "/content-tools"].includes(activePage);
  const audienceActive = ["/subscribers", "/segments"].includes(activePage);

  return (
    <aside
      style={{
        width: "175px",
        minHeight: "100%",
        backgroundColor: "#ffffff",
        padding: 0,
        boxShadow: "2px 0 5px rgba(0, 0, 0, 0.1)",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}>
      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
        }}>
        <NavGroup
          icon={<Mail size={20} />}
          label="Emails"
          isActive={emailsActive}
        >
          <NavItem
            icon={<CalendarIcon />}
            text="Posts"
            isActive={activePage === "/dashboard"}
            to="/dashboard"
            nested
          />
          <NavItem
            icon={<GitBranch size={20} />}
            text="Journeys"
            isActive={activePage === "/journeys"}
            to="/journeys"
            nested
          />
        </NavGroup>
        <NavItem
          icon={<FlourIcon />}
          text="Requests"
          isActive={activePage === "/requests"}
          to="/requests"
        />
        <NavGroup
          icon={<BookOpen size={20} />}
          label="Content"
          isActive={contentActive}
        >
          <NavItem
            icon={<PencilIcon />}
            text="Blueprints"
            isActive={activePage === "/blueprints"}
            to="/blueprints"
            nested
          />
          <NavItem
            icon={<ListIcon />}
            text="Sources"
            isActive={activePage === "/sources"}
            to="/sources"
            nested
          />
          {hasInterviews && (
            <NavItem
              icon={<MessageSquare size={20} />}
              text="Interviews"
              isActive={activePage === "/content-interviews"}
              to="/content-interviews"
              nested
            />
          )}
          {isOperator && (
            <NavItem
              icon={<Wrench size={20} />}
              text="Content Tools"
              isActive={activePage === "/content-tools"}
              to="/content-tools"
              nested
            />
          )}
        </NavGroup>
        <NavGroup
          icon={<Users size={20} />}
          label="Audience"
          isActive={audienceActive}
        >
          <NavItem
            icon={<SubscribersIcon />}
            text="Subscribers"
            isActive={activePage === "/subscribers"}
            to="/subscribers"
            nested
          />
          <NavItem
            icon={<Filter size={20} />}
            text="Segments"
            isActive={activePage === "/segments"}
            to="/segments"
            nested
          />
        </NavGroup>
        <NavItem
          icon={<Rocket size={20} />}
          text="Launch"
          isActive={activePage === "/launch"}
          to="/launch"
        />
        { !shouldHideAnalytics &&
          <NavItem
            icon={<GrowIcon />}
            text="Analytics"
            isActive={activePage === "/analytics"}
            to="/analytics"
          />
        }
      </ul>
      <div>
        {!isWhiteLabel() && (
          <a
            href="https://calendly.com/evan-withpotions/evan-15-min"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "6px",
              margin: "8px 10px",
              padding: "10px 8px",
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              textDecoration: "none",
              color: "#333",
              fontSize: "11px",
              lineHeight: 1.4,
              textAlign: "center",
            }}
          >
            <img
              src="/evan-headshot.jpeg"
              alt="Evan"
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
            <span>
              <strong style={{ fontSize: "12px", display: "block", marginBottom: "2px" }}>
                Book a free consultation
              </strong>
              <span style={{ color: "#666" }}>
                1:1 with Evan, co-founder & CEO — onboarding, deliverability & list strategy
              </span>
            </span>
          </a>
        )}
        <div
          className={`sidebar-settings ${activePage === "/settings" ? "active" : ""}`}>
          <NavItem
            icon={<SettingsIcon />}
            text="Settings"
            isActive={activePage === "/settings"}
            to="/settings"
          />
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
